import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useClassName } from 'common/hooks';
import HomepageAsset from 'public/screens/Home/Components/Asset';

import './vertical-carousel.less';
import useCustomStyle from '../../hooks';
import { getStylesByPrefix } from '../../utils';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toPx = (value, fallback = 0) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'number') return value;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const getOuterHeight = (el) => {
  if (!el) return 0;
  const computed = window.getComputedStyle(el);
  const marginTop = parseFloat(computed.marginTop) || 0;
  const marginBottom = parseFloat(computed.marginBottom) || 0;
  return el.offsetHeight + marginTop + marginBottom;
};

export default function VerticalCarousel({ component, onTravelChange }) {
  const classNames = useClassName('CLPVerticalCarousel');
  const [, style] = useCustomStyle(component?.fields?.settings);
  const items = component?.fields?.items || [];

  const viewportRef = useRef(null);
  const filmstripRef = useRef(null);
  const itemRefs = useRef([]);
  const rafRef = useRef(null);
  const prevSectionRef = useRef(null);
  const prevPinnedRef = useRef(false);
  const prevZIndexPinnedRef = useRef(null);
  const placeholderRef = useRef(null);
  const prevPinnedLayoutRef = useRef(null);

  const metricsRef = useRef({
    startY: 0,
    totalTravel: 0,
    reservedTravel: 0,
    viewportH: 0,
  });

  const boxRight = style?.['--carousel-right'] || '40px';
  const boxWidth = style?.['--carousel-width'] || '500px';

  const gap = toPx(style?.['--carousel-item-gap'], 24);
  const startOffset = toPx(style?.['--carousel-offset'], 0);
  const endOffset = toPx(style?.['--carousel-end-offset'], 100);
  const height = toPx(style?.['--carousel-height']);
  const baseImageStyle = getStylesByPrefix('--images-base-', style);

  const imageScales =
    style?.['--images-scale']?.split(',').map((e) => e.trim()) ||
    new Array(items.length).fill(1);
  const imageMoveX =
    style?.['--images-move-x']?.split(',').map((e) => e.trim()) ||
    new Array(items.length).fill(0);
  const imageMoveY =
    style?.['--images-move-y']?.split(',').map((e) => e.trim()) ||
    new Array(items.length).fill(0);
  const releaseDelay = toPx(style?.['--release-delay'], 0);

  const measure = () => {
    const viewport = viewportRef.current;
    const filmstrip = filmstripRef.current;
    if (!viewport || !filmstrip) return;

    const viewportH = viewport.offsetHeight || 0;

    let contentHeight = 0;
    itemRefs.current.forEach((el) => {
      if (!el) return;
      contentHeight = Math.max(contentHeight, el.offsetTop + el.offsetHeight);
    });

    const startY = viewportH + startOffset;
    const endY = -contentHeight - endOffset;
    const totalTravel = Math.max(0, startY - endY);
    const reservedTravel =
      totalTravel + getOuterHeight(prevSectionRef.current) + releaseDelay;

    const prevTravel = metricsRef.current.reservedTravel;
    metricsRef.current = { startY, totalTravel, reservedTravel, viewportH };

    if (onTravelChange && reservedTravel !== prevTravel) {
      onTravelChange(reservedTravel);
    }

    // Set sticky top on the carousel section container
    const scene = viewport.closest('.clp-section__carousel-scene');
    if (scene) {
      const container = scene.querySelector('.clp-section__container');
      if (container) {
        container.style.top = `calc(100dvh - ${height}px)`;
        container.style.overflow = `hidden`;
      }
    }
  };

  const update = () => {
    const viewport = viewportRef.current;
    const filmstrip = filmstripRef.current;
    if (!viewport || !filmstrip) return;

    const { startY, totalTravel, viewportH } = metricsRef.current;

    const scene = viewport.closest('.clp-section__carousel-scene');
    if (!scene) {
      filmstrip.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    const sceneRect = scene.getBoundingClientRect();
    const windowH = window.innerHeight;

    const scrolled = clamp(windowH - viewportH - sceneRect.top, 0, totalTravel);
    const currentTranslate = startY - scrolled;

    let extraMargin = 0;

    if (scene) {
      const container = scene.querySelector('.clp-section__container');
      if (container) {
        const maxBorderRadius = Math.max(
          toPx(container.style.borderTopLeftRadius),
          toPx(container.style.borderTopRightRadius)
        );
        extraMargin = maxBorderRadius;
      }
    }

    pinPrevSection(scrolled > 0, extraMargin);

    filmstrip.style.transform = `translate3d(0, ${currentTranslate}px, 0)`;

    itemRefs.current.forEach((item) => {
      if (!item) return;
      const visualTop = currentTranslate + item.offsetTop;
      item.style.transform =
        visualTop > viewportH * 0.4 ? 'scale(1)' : 'scale(1.15)';
    });
  };

  const pinPrevSection = (active, extraMargin) => {
    const el = prevSectionRef.current;
    const scene = viewportRef.current?.closest('.clp-section__carousel-scene');
    if (!el || !scene) return;
    const container = scene.querySelector('.clp-section__container');
    if (!container) return;

    if (active && !prevPinnedRef.current) {
      const rect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const computed = window.getComputedStyle(el);

      const marginTop = parseFloat(computed.marginTop) || 0;
      const marginBottom = parseFloat(computed.marginBottom) || 0;
      const marginLeft = parseFloat(computed.marginLeft) || 0;
      const marginRight = parseFloat(computed.marginRight) || 0;

      // Distancia visual real entre el bloque anterior y el contenedor sticky.
      const gapToContainer = containerRect.top - rect.bottom;
      const pinTop =
        containerRect.top - gapToContainer - rect.height + extraMargin;

      const placeholder = document.createElement('div');
      placeholder.style.height = `${rect.height + marginTop + marginBottom}px`;
      placeholder.style.width = `${rect.width + marginLeft + marginRight}px`;
      placeholder.style.pointerEvents = 'none';
      placeholder.style.visibility = 'hidden';
      placeholder.style.margin = '0';

      el.parentNode.insertBefore(placeholder, el.nextSibling);
      placeholderRef.current = placeholder;

      el.style.position = 'fixed';
      el.style.top = `${pinTop}px`;
      el.style.left = `${rect.left}px`;
      el.style.width = `${rect.width}px`;
      prevZIndexPinnedRef.current = computed.zIndex;
      el.style.zIndex = '-2';
      prevPinnedLayoutRef.current = {
        gapToContainer,
        height: rect.height,
        left: rect.left,
        width: rect.width,
      };

      prevPinnedRef.current = true;
    } else if (active && prevPinnedRef.current) {
      const layout = prevPinnedLayoutRef.current;
      if (!layout) return;

      const containerRect = container.getBoundingClientRect();
      const pinTop =
        containerRect.top - layout.gapToContainer - layout.height + extraMargin;

      el.style.top = `${pinTop}px`;
      el.style.left = `${layout.left}px`;
      el.style.width = `${layout.width}px`;
    } else if (!active && prevPinnedRef.current) {
      el.style.position = '';
      el.style.top = '';
      el.style.left = '';
      el.style.width = '';
      el.style.zIndex = prevZIndexPinnedRef.current;

      if (placeholderRef.current) {
        placeholderRef.current.remove();
        placeholderRef.current = null;
      }

      prevZIndexPinnedRef.current = null;
      prevPinnedLayoutRef.current = null;
      prevPinnedRef.current = false;
    }
  };

  const requestUpdate = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(update);
  };

  useLayoutEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
    measure();
    requestUpdate();
  }, [items.length, boxWidth, boxRight, gap, startOffset, endOffset, height]);

  useEffect(() => {
    // Cache reference to the previous sibling of the carousel scene
    const viewport = viewportRef.current;
    if (viewport) {
      const scene = viewport.closest('.clp-section__carousel-scene');
      if (scene) {
        prevSectionRef.current = scene.previousElementSibling;
      }
    }

    const onScroll = () => requestUpdate();
    const onResize = () => {
      measure();
      requestUpdate();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        measure();
        requestUpdate();
      });
      if (viewportRef.current) ro.observe(viewportRef.current);
      if (filmstripRef.current) ro.observe(filmstripRef.current);
      itemRefs.current.forEach((el) => el && ro.observe(el));
    }

    measure();
    requestUpdate();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (onTravelChange) onTravelChange(0);
      pinPrevSection(false);
    };
  }, [
    items.length,
    boxWidth,
    boxRight,
    gap,
    startOffset,
    endOffset,
    height,
    onTravelChange,
  ]);

  if (!items.length) return null;

  return (
    <div
      ref={viewportRef}
      className={classNames('viewport')}
      style={{
        right: boxRight,
        width: boxWidth,
      }}>
      <div
        ref={filmstripRef}
        className={classNames('filmstrip')}
        style={{ gap: `${gap}px` }}>
        {items.map((item, index) => {
          const thisImageStyle = {
            transform: `scale(${imageScales[index] || 1}) translateX(${
              imageMoveX[index] || 0
            }) translateY(${imageMoveY[index] || 0})`,
          };

          return (
            <div
              key={item?.sys?.id || index}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={classNames('item')}>
              <HomepageAsset
                asset={item}
                className={classNames('asset')}
                style={{ ...baseImageStyle, ...thisImageStyle }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
